import type { ExperimentMonitorVariant, ExperimentMonitorStat } from '@/state/experiments/useExperimentsMonitor';
import { NO_CURRENCY } from 'helpers/currency';

export type AggregatedMetrics = {
  totalSessions: number;
  totalConversions: number;
  totalRevenue: number;
  conversionRate: number;
  /** Null when the revenue was recorded without a currency: a figure with no unit to name. */
  primaryCurrency: string | null;
};

export type CurrencyStats = {
  sessions: number;
  conversions: number;
  revenue: number;
};

type ComputeAggregatedMetricsParams = {
  variants: ExperimentMonitorVariant[];
  stats: ExperimentMonitorStat[];
};

export function computeAggregatedMetrics({ variants, stats }: ComputeAggregatedMetricsParams): AggregatedMetrics {
  let totalSessions = 0;
  let totalConversions = 0;

  for (const variant of variants) {
    totalSessions += variant.visitorCount?.count || 0;
  }

  const revenueByCurrency: Record<string, number> = {};
  for (const stat of stats) {
    totalConversions += stat.conversions || 0;
    const currency = stat.currencyCode || NO_CURRENCY;
    revenueByCurrency[currency] = (revenueByCurrency[currency] || 0) + (stat.revenue || 0);
  }

  // The largest bucket wins, including the no-currency one.
  //
  // It was skipped before, and the fallback when nothing else matched was a literal
  // 'USD'. So a site that never sends a currency has all of its revenue under the
  // no-currency bucket, took the fallback, and then read revenueByCurrency['USD'] --
  // absent -- and reported 0, however much money was actually there.
  let primaryCurrency: string | null = null;
  let maxRevenue = 0;
  for (const [currency, revenue] of Object.entries(revenueByCurrency)) {
    if (revenue > maxRevenue) {
      maxRevenue = revenue;
      primaryCurrency = currency === NO_CURRENCY ? null : currency;
    }
  }

  const totalRevenue = maxRevenue;
  const conversionRate = totalSessions > 0 ? (totalConversions / totalSessions) * 100 : 0;

  return { totalSessions, totalConversions, totalRevenue, conversionRate, primaryCurrency };
}

type ComputeStatsByCurrencyParams = {
  stats: ExperimentMonitorStat[];
  totalSessions: number;
};

export function computeStatsByCurrency({
  stats,
  totalSessions,
}: ComputeStatsByCurrencyParams): Record<string, CurrencyStats> {
  const byCurrency: Record<string, CurrencyStats> = {};

  for (const stat of stats) {
    const currency = stat.currencyCode || NO_CURRENCY;
    if (!byCurrency[currency]) {
      byCurrency[currency] = { sessions: 0, conversions: 0, revenue: 0 };
    }
    byCurrency[currency].conversions += stat.conversions || 0;
    byCurrency[currency].revenue += stat.revenue || 0;
  }

  const currencies = Object.keys(byCurrency);
  if (currencies.length > 0) {
    const sessionsPerCurrency = Math.floor(totalSessions / currencies.length);
    for (let i = 0; i < currencies.length; i++) {
      const key = currencies[i];
      const entry = key ? byCurrency[key] : undefined;
      if (!entry) continue;
      entry.sessions = i === 0 ? totalSessions - sessionsPerCurrency * (currencies.length - 1) : sessionsPerCurrency;
    }
  }

  return byCurrency;
}
